import { localDayExpiry } from "./expiry";
import { useAuthStore } from "@/stores/auth";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import type { CreatedShareLink } from "@/lib/types";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useGuestLinks } from "./useGuestLinks";

export const expiryLabel = (value: number | null) =>
  value === null
    ? "No expiry · until revoked"
    : `Until ${new Date(value * 1000).toLocaleDateString("en-CA")} · end of day`;
const EXPIRIES = [
  { value: "12", label: "Match day (12 hours)" },
  { value: "24", label: "24 hours" },
  { value: "168", label: "7 days" },
  { value: "date", label: "Choose date" },
  { value: "none", label: "No expiry" },
];
export function GuestLinksSection({
  teamId,
  initialExpiry = "24",
  mode = "all",
  linkId,
  presentation = "default",
  teamName,
  onRequestClose,
}: {
  teamId: string;
  initialExpiry?: "24" | "date";
  mode?: "all" | "create" | "manage";
  linkId?: string;
  presentation?: "default" | "admin";
  teamName?: string;
  onRequestClose?: () => void;
}) {
  const { list, create, revoke, identity } = useGuestLinks(teamId);
  const timezone =
    useAuthStore((s) => s.profile?.timezone) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const toast = useToast();
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState<string>(initialExpiry);
  const [date, setDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    identity: string;
    link: CreatedShareLink;
  } | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  useEffect(() => {
    setExpiry(initialExpiry);
    setCreated(null);
    setLabel("");
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    setDate(
      presentation === "admin"
        ? [
            defaultDate.getFullYear(),
            String(defaultDate.getMonth() + 1).padStart(2, "0"),
            String(defaultDate.getDate()).padStart(2, "0"),
          ].join("-")
        : "",
    );
    setError(null);
    setRevokeId(null);
  }, [identity, initialExpiry, presentation]);
  const current = created?.identity === identity ? created.link : null;
  const links = (list.data?.links ?? []).filter(
    (link) => !linkId || link.id === linkId,
  );
  const collection = useCollection(links, {
    scope: identity,
    text: (l) =>
      `${l.label ?? "Team viewing"} ${expiryLabel(l.expires_at)} ${l.created_at}`,
    matches: (l, f) =>
      f === "active"
        ? l.active
        : f === "revoked"
          ? !!l.revoked_at
          : !l.active && !l.revoked_at,
  });
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (!label.trim()) throw new Error("Enter a link label");
      const mode =
        expiry === "none"
          ? { no_expiry: true }
          : expiry === "date"
            ? { expires_at: localDayExpiry(date, timezone) }
            : { ttl_hours: Number(expiry) };
      if (mode.expires_at !== undefined && mode.expires_at <= Date.now() / 1000)
        throw new Error("Choose a future expiry date");
      const link = await create.mutateAsync({ label: label.trim(), ...mode });
      setCreated({ identity, link });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create link");
    }
  };
  const url = current ? `${window.location.origin}/guest#${current.token}` : "";
  const managedLink = mode === "manage" ? links[0] : undefined;
  const expiryOptions =
    presentation === "admin"
      ? [
          { value: "date", label: "Custom expiry date" },
          { value: "none", label: "No expiry" },
        ]
      : EXPIRIES;
  return (
    <section
      className={`guest-links-section guest-links-${mode} rounded-panel border border-rule bg-surface p-5`}
      data-testid="settings-section-guest-links"
      aria-labelledby="settings-guest-links-heading"
    >
      <h3
        id="settings-guest-links-heading"
        className={mode === "all" ? undefined : "sr-only"}
      >
        Guest viewing links
      </h3>
      {mode !== "manage" ? (
        <p className="guest-link-intro mt-2 max-w-2xl text-sm text-ink-muted">
          {presentation === "admin" ? (
            <>
              A guest link allows viewing of {teamName ?? "this squad"} live
              streams and match recordings. It does not grant account or
              coaching access.
            </>
          ) : (
            <>
              Let people without accounts watch this team’s live streams and
              match recordings. Anyone with the link can watch until it expires
              or you revoke it. Copy the reusable link when it is created; it is
              shown only once.
            </>
          )}
        </p>
      ) : null}
      {mode !== "manage" && !current ? (
        <form
          onSubmit={(event) => void submit(event)}
          className="guest-create"
          data-custom-date={expiry === "date"}
        >
          <label className="grid gap-1 text-sm">
            Link label{presentation === "admin" ? " *" : ""}
            <input
              required
              maxLength={80}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={
                presentation === "admin"
                  ? "Weekend family viewing"
                  : "Family viewing"
              }
              data-testid="guest-link-label"
            />
          </label>
          <label className="grid gap-1 text-sm">
            Expiry
            <TkticSelect
              ariaLabel="Guest link duration"
              value={expiry}
              onChange={setExpiry}
              options={expiryOptions}
              data-testid="guest-link-ttl"
            />
          </label>
          {expiry === "date" ? (
            <label className="grid gap-1 text-sm">
              Expiry date{presentation === "admin" ? " *" : ""}
              <TkticDateInput
                value={date}
                onChange={setDate}
                data-testid="guest-link-date"
              />
            </label>
          ) : null}
          {expiry === "date" ? (
            <p className="guest-helper text-xs text-ink-muted">
              {presentation === "admin"
                ? `Valid through the end of this date (${timezone}).`
                : `Ends at 11:59:59 PM in ${timezone}. Dates beyond seven days are supported.`}
            </p>
          ) : expiry === "none" ? (
            <p className="guest-helper text-xs text-ink-muted">
              {presentation === "admin"
                ? "No expiry · until revoked. You can revoke this link at any time."
                : "Until revoked. You can revoke access at any time."}
            </p>
          ) : null}
          <div className="guest-create-actions">
            {presentation === "admin" && onRequestClose ? (
              <Button type="button" variant="ghost" onClick={onRequestClose}>
                Cancel
              </Button>
            ) : null}
            <Button
              type="submit"
              disabled={create.isPending}
              data-testid="guest-link-create"
            >
              {create.isPending
                ? "Creating…"
                : presentation === "admin"
                  ? "Create local link"
                  : "Create guest link"}
            </Button>
          </div>
        </form>
      ) : null}
      {current ? (
        <div
          className="mt-4 rounded-inset border border-rule bg-field p-4"
          data-testid="guest-link-created"
        >
          <strong>{current.label ?? "Team viewing"}</strong>
          <p className="text-sm">
            Live streams and match recordings ·{" "}
            {expiryLabel(current.expires_at)}
          </p>
          <code
            className="my-3 block break-all text-sm"
            data-testid="guest-link-created-url"
          >
            {url}
          </code>
          <div className="flex gap-2">
            <Button
              data-testid="guest-link-created-copy"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Link copied");
                } catch {
                  setError(
                    "Clipboard unavailable. Select and copy the displayed link.",
                  );
                }
              }}
            >
              Copy link
            </Button>
            <Button variant="ghost" onClick={() => setCreated(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="mt-3 text-sm text-destructive"
          data-testid="guest-link-error"
        >
          {error}
        </p>
      ) : null}
      {list.isError ? (
        <p role="alert">
          Could not load links.{" "}
          <button onClick={() => void list.refetch()}>Retry</button>
        </p>
      ) : list.isLoading ? (
        <p role="status">Loading links…</p>
      ) : null}
      {mode === "manage" && managedLink ? (
        <div
          className="admin-guest-manage"
          data-testid="guest-link-row"
          data-active={managedLink.active}
        >
          <div className="admin-guest-manage-heading">
            <div>
              <p className="eyebrow">{teamName ?? "Squad"}</p>
              <h3>{managedLink.label ?? "Team viewing"}</h3>
              <p className="text-sm text-ink-muted">
                {expiryLabel(managedLink.expires_at)}
              </p>
            </div>
            <Badge variant={managedLink.active ? "good" : "muted"}>
              {managedLink.revoked_at
                ? "Revoked"
                : managedLink.active
                  ? "Active"
                  : "Expired"}
            </Badge>
          </div>
          <p className="text-sm">
            Guest access covers this squad’s live streams and match recordings.
            It does not grant account or coaching access.
          </p>
          <p className="admin-guest-security-note text-sm text-ink-muted">
            For security, the reusable link is only shown when it is created.
          </p>
          <div className="admin-guest-manage-actions">
            {managedLink.active && !revokeId ? (
              <Button
                variant="ghost"
                data-testid="guest-link-revoke"
                onClick={() => setRevokeId(managedLink.id)}
              >
                Revoke link
              </Button>
            ) : null}
            {onRequestClose ? (
              <Button variant="ghost" onClick={onRequestClose}>
                Close
              </Button>
            ) : null}
          </div>
        </div>
      ) : mode !== "create" ? (
        <>
          <CollectionToolbar
            collection={collection}
            label="guest links"
            filters={[
              { value: "", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "expired", label: "Expired" },
              { value: "revoked", label: "Revoked" },
            ]}
          />
          <ul className="mt-4 grid gap-2" data-testid="guest-link-list">
            {collection.items.map((link) => (
              <li
                key={link.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-inset border border-rule p-3"
                data-testid="guest-link-row"
                data-active={link.active}
              >
                <div className="min-w-0">
                  <strong className="break-words text-sm">
                    {link.label ?? "Team viewing"}
                  </strong>
                  <p className="text-xs text-ink-muted">
                    {expiryLabel(link.expires_at)} · Created{" "}
                    {new Date(link.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="guest-link-actions">
                  <Badge variant={link.active ? "good" : "muted"}>
                    {link.revoked_at
                      ? "Revoked"
                      : link.active
                        ? "Active"
                        : "Expired"}
                  </Badge>
                  {link.active ? (
                    <Button
                      variant="ghost"
                      data-testid="guest-link-revoke"
                      disabled={revoke.isPending}
                      onClick={() => setRevokeId(link.id)}
                    >
                      Revoke
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          {!list.isLoading && !list.isError && collection.items.length === 0 ? (
            <p className="mt-3 text-sm text-ink-muted">
              {links.length ? "No matching links." : "No guest links yet."}
            </p>
          ) : null}
          <CollectionPagination collection={collection} label="guest links" />
        </>
      ) : null}
      {revokeId ? (
        <ConfirmDialog
          title="Revoke guest link?"
          message="This link will immediately stop granting access to this team’s live streams and match recordings."
          confirmLabel="Revoke link"
          onClose={() => setRevokeId(null)}
          confirmDisabled={revoke.isPending}
          danger
          onConfirm={async () => {
            try {
              await revoke.mutateAsync(revokeId);
              if (current?.id === revokeId) setCreated(null);
              setRevokeId(null);
            } catch (err) {
              setError(
                err instanceof Error ? err.message : "Could not revoke link",
              );
            }
          }}
        />
      ) : null}
    </section>
  );
}
